import React from 'react';
import { Box, Typography } from '@mui/material';
import { Placar, SetPlacar } from '../../types/placar';

export interface MatchSetsResultProps {
  /** O placar com os sets */
  placar?: Placar | { sets?: SetPlacar[] | null } | null;
  /** Se a equipe A teve WO (Walkover) */
  equipeAWO?: boolean | null;
  /** Se a equipe B teve WO (Walkover) */
  equipeBWO?: boolean | null;
  /** Se a equipe A desistiu */
  equipeADesistencia?: boolean | null;
  /** Se a equipe B desistiu */
  equipeBDesistencia?: boolean | null;
  /** Se deve usar Material-UI (padrão: false, usa Bootstrap) */
  useMaterialUI?: boolean;
  /** Classe CSS adicional (para Bootstrap) */
  className?: string;
  /** Estilo inline adicional */
  style?: React.CSSProperties;
}

/**
 * Componente reutilizável para exibir o resultado de uma partida mostrando os sets.
 * Cada set exibe gamesA x gamesB, com tiebreaks em fonte menor no canto inferior direito se existirem.
 * 
 * Suporta renderização com Material-UI ou Bootstrap/HTML simples.
 */
const MatchSetsResult: React.FC<MatchSetsResultProps> = ({
  placar,
  equipeAWO,
  equipeBWO,
  equipeADesistencia,
  equipeBDesistencia,
  useMaterialUI = false,
  className = '',
  style,
}) => {
  // Verificar se há WO ou desistência
  const hasWO = equipeAWO === true || equipeBWO === true;
  const hasDesistencia = equipeADesistencia === true || equipeBDesistencia === true;

  // Se houver WO, mostrar "WO"
  if (hasWO) {
    if (useMaterialUI) {
      return (
        <Typography variant="body2" color="warning.main" sx={style} className={className}>
          WO
        </Typography>
      );
    }
    return (
      <span className={`text-warning fw-bold ${className}`} style={style}>
        WO
      </span>
    );
  }

  // Se houver desistência, mostrar "D"
  if (hasDesistencia) {
    if (useMaterialUI) {
      return (
        <Typography variant="body2" color="error.main" sx={style} className={className}>
          D
        </Typography>
      );
    }
    return (
      <span className={`text-danger fw-bold ${className}`} style={style}>
        D
      </span>
    );
  }

  // Verificar se há placar com sets
  if (!placar || !placar.sets || placar.sets.length === 0) {
    if (useMaterialUI) {
      return (
        <Typography variant="body2" color="text.secondary" sx={style} className={className}>
          Pendente
        </Typography>
      );
    }
    return (
      <span className={`text-muted ${className}`} style={style}>
        Pendente
      </span>
    );
  }

  // Filtrar nulos, ordenar por numeroSet e manter apenas sets com valor preenchido
  const setsComValor = [...placar.sets]
    .filter((set): set is SetPlacar => set !== null && set !== undefined)
    .sort((a, b) => a.numeroSet - b.numeroSet)
    .filter((set) => set.gamesA !== null || set.gamesB !== null);

  if (setsComValor.length === 0) {
    if (useMaterialUI) {
      return (
        <Typography variant="body2" color="text.secondary" sx={style} className={className}>
          Pendente
        </Typography>
      );
    }
    return (
      <span className={`text-muted ${className}`} style={style}>
        Pendente
      </span>
    );
  }

  // Renderizar com Material-UI
  if (useMaterialUI) {
    return (
      <Box display="flex" flexDirection="column" gap={0.5} sx={style} className={className}>
        {setsComValor.map((set) => {
          const gamesA = set.gamesA ?? '-';
          const gamesB = set.gamesB ?? '-';
          const temTiebreak = (set.tiebreakA !== null && set.tiebreakA !== undefined) || 
                             (set.tiebreakB !== null && set.tiebreakB !== undefined);

          return (
            <Box
              key={set.numeroSet}
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              {/* Games A com tiebreak */}
              <Box 
                position="relative" 
                display="inline-flex" 
                alignItems="flex-end"
              >
                <Typography variant="body2" component="span">
                  {gamesA}
                </Typography>
                {temTiebreak && set.tiebreakA !== null && set.tiebreakA !== undefined && (
                  <Typography
                    variant="caption"
                    component="span"
                    sx={{
                      fontSize: '0.6rem',
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      lineHeight: 1,
                      transform: 'translate(100%, 0%)',
                    }}
                  >
                    {set.tiebreakA}
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" component="span" sx={{ mx: 0.25 }}>
                x
              </Typography>

              {/* Games B com tiebreak */}
              <Box 
                position="relative" 
                display="inline-flex" 
                alignItems="flex-end"
              >
                <Typography variant="body2" component="span">
                  {gamesB}
                </Typography>
                {temTiebreak && set.tiebreakB !== null && set.tiebreakB !== undefined && (
                  <Typography
                    variant="caption"
                    component="span"
                    sx={{
                      fontSize: '0.6rem',
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      lineHeight: 1,
                      transform: 'translate(100%, 0%)',
                    }}
                  >
                    {set.tiebreakB}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  // Renderizar com Bootstrap/HTML simples
  return (
    <div className={`d-flex flex-column gap-1 ${className}`} style={style}>
      {setsComValor.map((set) => {
        const gamesA = set.gamesA ?? '-';
        const gamesB = set.gamesB ?? '-';
        const temTiebreak = (set.tiebreakA !== null && set.tiebreakA !== undefined) || 
                           (set.tiebreakB !== null && set.tiebreakB !== undefined);

        return (
          <div key={set.numeroSet} className="d-flex align-items-center gap-1">
            {/* Games A com tiebreak */}
            <span className="position-relative d-inline-flex align-items-end">
              <span className="fw-bold">{gamesA}</span>
              {temTiebreak && set.tiebreakA !== null && set.tiebreakA !== undefined && (
                <span
                  style={{
                    fontSize: '0.6rem',
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    lineHeight: 1,
                    transform: 'translate(100%, 0%)',
                  }}
                >
                  {set.tiebreakA}
                </span>
              )}
            </span>

            <span className="text-muted mx-1">x</span>

            {/* Games B com tiebreak */}
            <span className="position-relative d-inline-flex align-items-end">
              <span className="fw-bold">{gamesB}</span>
              {temTiebreak && set.tiebreakB !== null && set.tiebreakB !== undefined && (
                <span
                  style={{
                    fontSize: '0.6rem',
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    lineHeight: 1,
                    transform: 'translate(100%, 0%)',
                  }}
                >
                  {set.tiebreakB}
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default MatchSetsResult;

